import type { settings as pt } from '../pt/settings';
// English mirror of ../pt/settings.ts (shape checked against its pt twin).
export const settings: typeof pt = {
  common: {
    saving: 'Saving…',
    saved: 'Saved ✓',
    moveUp: 'Move up',
    moveDown: 'Move down',
  },
  global: {
    title: 'Global Configuration',
    subtitle: 'Institution details, locations and portal branding',
    adminOnly: 'This area is restricted to portal administrators.',
    institution: 'Institution',
    name: 'Institution name',
    nif: 'NIF',
    phone: 'Landline',
    mobile: 'Mobile',
    email: 'Email',
    branding: 'Portal branding',
    noImage: 'No image',
    upload: 'Upload',
    imageTooLarge: 'Image too large (max. 4 MB).',
    imageReadError: 'Could not read the image.',
    asset: {
      logo: { label: 'Logo (login screen)', hint: 'PNG/SVG with transparent background' },
      icon: { label: 'Web app icon (favicon)', hint: 'Small square image' },
      loginBackground: { label: 'Login screen background', hint: 'Background image' },
    },
    locations: 'Locations',
    deleteLocationConfirm: 'Delete this location?',
    locName: 'Name *',
    locNamePlaceholder: 'Headquarters',
    locAbbr: 'Abbreviation',
    locAbbrPlaceholder: 'HQ',
    locAddress: 'Address',
    colName: 'Name',
    colAbbr: 'Abbreviation',
    colAddress: 'Address',
  },
  fields: {
    title: 'Record Fields',
    translateMissing: 'Translate missing labels',
    translateMissingNone: 'No labels to translate.',
    translateMissingDone: '{n} labels translated.',
    subtitle: 'Tabs, fields, required, reserved and onboarding',
    intro: {
      lead: 'Central management of the record. The base fields come from SISTRADE; here you can organise the ',
      tabs: 'tabs',
      afterTabs:
        ' (create, rename, reorder), move each field to the right tab, reorder, and mark which are ',
      required: 'required',
      afterRequired: ', ',
      reserved: 'reserved',
      afterReserved: ' or appear on the ',
      onboarding: 'onboarding',
      afterOnboarding:
        ' form (what the new collaborator fills in via the link). The onboarding requirement is the same as the one marked here.',
    },
    type: {
      text: 'Text',
      longText: 'Long text',
      number: 'Number',
      integer: 'Integer',
      date: 'Date',
      dateTime: 'Date/time',
      boolean: 'Yes/No',
      select: 'Selection',
      multiSelect: 'Multiple selection',
      email: 'Email',
      phone: 'Phone',
      url: 'URL',
      currency: 'Currency (EUR)',
      percent: 'Percentage',
    },
    moveSectionUp: 'Move tab up',
    moveSectionDown: 'Move tab down',
    rename: 'Rename',
    renamePrompt: 'New tab name',
    sectionReservedHint: 'Section visible only to those with permission',
    reservedLabel: 'reserved',
    noFields: 'No fields in this section.',
    baseTag: 'base',
    fieldCount: '{n} fields',
    fieldCountOne: '1 field',
    expandSection: 'Expand section',
    collapseSection: 'Collapse section',
    flag: {
      required: 'Required',
      reserved: 'Reserved',
      onboarding: 'Onboarding',
    },
    removeConfirm: 'Disable this field? Existing values are kept.',
    col: {
      field: 'Field',
      type: 'Type',
      required: 'Req.',
      reserved: 'Rsvd.',
      onboarding: 'Onboarding',
      onboardingHint: 'Appears on the onboarding form (new collaborator)',
      section: 'Tab',
      order: 'Order',
    },
    newSection: '+ New tab',
    newSectionTitle: 'New tab',
    sectionName: 'Tab name',
    createSection: 'Create tab',
    creating: 'Creating…',
    newField: '+ New field',
    newFieldIn: 'New field in “{section}”',
    fieldName: 'Field name',
    typeLabel: 'Type',
    options: 'Options',
    optionsPlaceholder: 'Option A\nOption B',
    requiredField: 'Required field',
    reservedField: 'Reserved field (visible only to those with permission)',
    onboardingField: 'Appears on the onboarding form (new collaborator)',
    createField: 'Create field',
  },
  sistrade: {
    title: 'SISTRADE Integration',
    subtitle: 'Connection status and collaborator synchronisation',
    mode: 'Mode',
    configured: 'Configured',
    baseUrl: 'Base URL',
    pullTitle: 'Sync from SISTRADE (pull)',
    pullDesc:
      'Pulls active collaborators from SISTRADE and updates the records in the portal (creates new ones, updates existing ones by code).',
    syncing: 'Syncing…',
    syncCollaborators: 'Sync collaborators',
    running:
      'The sync runs in the background — you can leave this page; the status is updated here automatically.',
    resultBase: '{total} collaborators — {created} created, {updated} updated',
    resultSkipped: ', {skipped} skipped',
    resultFields: ', {fields} fields filled in',
    resultReduced: ' (reduced API data)',
    syncFailed: 'The sync failed: {error}.',
    unknownError: 'unknown error',
    adminOnly: 'Synchronisation with SISTRADE is restricted to portal administrators.',
    teamsTitle: 'Scheduled team synchronisation',
    teamsDesc:
      'Research teams and lines are stored in the portal database and updated automatically from SISTRADE. Last sync:',
    never: 'never',
    autoSync: 'Synchronise automatically',
    every: 'Every',
    hours: 'hours',
    syncTeamsNow: 'Sync teams now',
    teamsSyncFailed: 'Team synchronisation failed: {error}',
  },
  dashboard: {
    title: 'Dashboard',
    subtitle:
      'Cards and charts each recipient sees on the Dashboard — by profile, group (AD) or person',
    sectionTitleLabel: 'Cards section title',
    noGroups: 'No AD groups',
    pickPerson: 'Choose person…',
    pickSubjectError: 'Choose the recipient (profile, group or person).',
    emptyWithSubject:
      'No cards for this recipient. The Dashboard shows the base indicators; add cards to customise it.',
    emptyNoSubject: 'Choose a recipient (profile, group or person) to configure their cards.',
    badgeCount: 'Count',
    badgeChart: 'Chart',
    cardTitle: 'Title',
    chartLabel: 'Chart',
    addCount: '+ Count',
    addChart: '+ Chart',
    newCount: 'New count',
    subject: {
      role: 'Profile',
      group: 'Group',
      person: 'Person',
    },
    role: {
      onboarding: 'Onboarding',
      hr: 'Human Resources',
      researcher: 'Researcher',
      direction: 'Board',
      admin: 'Administration',
    },
    source: {
      label: 'Source',
      people: 'Collaborators',
      articles: 'Publications',
    },
    chart: {
      started: 'Contracts started',
      ended: 'Contracts ended',
      yearCompare: 'Previous year vs current',
      fieldDistribution: 'Distribution by field',
      pubsByYear: 'Publications by year',
      pubsByMonth: 'Publications by month (current year)',
      pubsYearCompare: 'Publications: previous year vs current',
      quartileDist: 'Distribution by quartile',
      pubsByTeam: 'Publications by team',
      topResearchers: 'Top researchers',
      citationsByYear: 'Citations by year',
    },
    metricLabel: 'Metric',
    metric: {
      count: 'Publications (total)',
      citations: 'Citations (total)',
      citationsPerPub: 'Citations per publication',
      percentQ1: '% in Q1',
      percentQ1Q2: '% in Q1+Q2',
      percentOpenAccess: '% Open access',
      researchers: 'Researchers with publications',
      pubsPerResearcher: 'Publications per researcher',
      pubsThisYear: 'Publications this year',
      pubsGrowthRate: 'Projected growth rate',
    },
    field: 'Field',
    value: 'Value',
    valuePlaceholder: 'value…',
    match: 'Match',
    matchEq: 'Equals',
    matchNe: 'Not equal (≠)',
    matchContains: 'Contains',
    chartStyle: 'Style',
    style: {
      bar: 'Bars',
      hbar: 'Horizontal bars',
      donut: 'Donut',
    },
    appearance: 'Appearance',
    icon: 'Icon',
    activeOnly: 'Active collaborators only',
  },
  it: {
    intro:
      'IT tools: sign in as a user to reproduce or help with what they see, and manage who the IT administrators are. Impersonation grants effective global access and is audited.',
    impersonate: {
      title: 'Sign in as a user',
      hint: 'Pick the collaborator and the roles to assume. Never Admin nor another IT admin.',
      searchPlaceholder: 'Search by name or email…',
      noResults: 'No collaborators with a professional email.',
      rolesLabel: 'Roles to assume',
      start: 'Sign in as {name}',
      starting: 'Signing in…',
    },
    role: {
      researcher: 'Researcher',
      onboarding: 'Onboarding',
      hr: 'Human Resources',
      direction: 'Direction',
    },
    list: {
      title: 'IT administrators',
      hint: 'Accounts that may impersonate users. Keep the list short.',
      usernamePlaceholder: 'username (e.g. name@ciimar.up.pt)',
      namePlaceholder: 'Name (optional)',
      add: 'Add',
      removeConfirm: 'Remove {username} from IT administrators?',
    },
  },
};
